import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Ladybug } from "@/components/site/Ladybug";

const fmt = (iso, opts) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", opts);

export const useAccountability = () => {
  const [data, setData] = useState(null);
  const load = () => api.get("/accountability").then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, []);
  return [data, load];
};

const Board = ({ board, me, limit }) => {
  const rows = limit ? board.slice(0, limit) : board;
  if (!rows.length) return <p className="text-sm text-[#232b1b]/60">No one has checked in yet this week. Be the first light.</p>;
  return (
    <ol className="space-y-3" data-testid="accountability-board">
      {rows.map((r, i) => (
        <li key={r.user_id} data-testid={`board-row-${r.user_id}`} className={`flex items-center gap-4 rounded-2xl px-4 py-3 ${r.user_id === me ? "bg-[#b6689a]/10" : "bg-[#faf7f2]"}`}>
          <span className={`font-serif-h text-xl w-6 ${i < 3 ? "text-[#b6689a]" : "text-[#232b1b]/45"}`}>{i + 1}</span>
          <span className="flex-1 text-sm font-medium">{r.name}{r.user_id === me && <span className="text-[#232b1b]/50 font-normal"> (you)</span>}</span>
          <div className="w-28 sm:w-40 h-2 rounded-full bg-[#dacac1]/60 overflow-hidden">
            <div className="h-full bg-[#577731] rounded-full transition-[width] duration-700" style={{ width: `${r.score * 10}%` }} />
          </div>
          <span className="text-sm font-semibold text-[#577731] w-12 text-right">{r.score}/10</span>
        </li>
      ))}
    </ol>
  );
};

const StreakNote = ({ streak }) => {
  const into = streak % 4;
  return (
    <div data-testid="streak-note" className="mt-6 rounded-2xl bg-[#b1cc90]/25 px-5 py-4 text-sm text-[#232b1b]/85">
      <strong className="text-[#577731]">4 weeks straight = 30% off at The Healers Market.</strong>{" "}
      {streak > 0 && !into
        ? "You completed a round! Your gratitude code is waiting in your Messages."
        : into === 0
          ? "Fill out this week to begin your first round. Your code arrives in your Messages when the 4th week closes."
          : `You are ${into} of 4 weeks into this round. Your code arrives in your Messages when the 4th week closes.`}
    </div>
  );
};

export const AccountabilityCard = () => {
  const { user } = useAuth();
  const [data] = useAccountability();
  if (!data) return null;
  return (
    <section data-testid="accountability-card" className="relative card-soft p-7 sm:p-9 mt-12">
      <Ladybug spot="tr" size={20} rotate={-30} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="eyebrow">Weekly Accountability</span>
          <h2 className="font-serif-h text-3xl text-[#577731] mt-3 flex items-center gap-3"><Trophy size={22} className="text-[#b6689a]" /> The Circle Scoreboard</h2>
          <p className="text-sm text-[#232b1b]/60 mt-2">Week of {fmt(data.week_start, { month: "long", day: "numeric" })} · renews every Saturday</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-widest text-[#232b1b]/55">Your week</p>
          <p className="font-serif-h text-5xl text-[#b6689a]" data-testid="accountability-my-score">{data.my_score}<span className="text-2xl text-[#232b1b]/40">/10</span></p>
        </div>
      </div>
      <div className="mt-6"><Board board={data.board} me={user.id} limit={5} /></div>
      <StreakNote streak={data.streak} />
      <Link to="/members/accountability" data-testid="accountability-open-btn" className="btn btn-olive mt-6">Check In on My Practice</Link>
    </section>
  );
};

const Cell = ({ on, disabled, onClick, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`h-9 w-9 rounded-full border flex items-center justify-center transition-colors duration-300 ${on ? "bg-[#577731] border-[#577731] text-white" : disabled ? "border-[#dacac1]/60 bg-transparent opacity-40 cursor-not-allowed" : "border-[#dacac1] hover:border-[#b6689a] hover:bg-[#b6689a]/10"}`}>
    {on && <Check size={16} />}
  </button>
);

export const Accountability = () => {
  const { user } = useAuth();
  const [data, load] = useAccountability();
  if (!data) return null;
  const toggle = async (practice, day) => {
    try {
      await api.post("/accountability/toggle", { practice, day });
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div data-testid="members-accountability">
      <span className="eyebrow">Weekly Accountability</span>
      <h1 className="font-serif-h font-light text-4xl sm:text-5xl text-[#577731] mt-4">Tend your practice, day by day</h1>
      <p className="mt-4 text-[#232b1b]/70 max-w-2xl">Mark each practice as you complete it. Your week begins fresh every Saturday and is scored from 1, just beginning, to 10, full completion. Your score joins the circle scoreboard so we can lift one another up.</p>
      <StreakNote streak={data.streak} />
      <div className="card-soft p-5 sm:p-8 mt-10 overflow-x-auto">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-[#232b1b]/60">Week of {fmt(data.week_start, { month: "long", day: "numeric" })}</p>
          <p className="font-serif-h text-3xl text-[#b6689a]" data-testid="accountability-page-score">{data.my_score}<span className="text-xl text-[#232b1b]/40">/10</span></p>
        </div>
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr>
              <th className="text-left font-normal pb-4" />
              {data.days.map((d, i) => (
                <th key={d} className={`pb-4 font-normal text-xs ${i === data.today_index ? "text-[#b6689a] font-semibold" : "text-[#232b1b]/60"}`}>
                  {fmt(d, { weekday: "short" })}<br />{fmt(d, { day: "numeric" })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.practices.map((p) => (
              <tr key={p.key} className="border-t border-[#dacac1]/70" data-testid={`practice-row-${p.key}`}>
                <td className="py-3 pr-4 font-medium text-[#232b1b]">{p.label}</td>
                {data.days.map((d, i) => (
                  <td key={d} className="py-3 text-center">
                    <div className="flex justify-center">
                      <Cell on={(data.mine[p.key] || []).includes(i)} disabled={i > data.today_index} onClick={() => toggle(p.key, i)} testid={`check-${p.key}-${i}`} />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-soft p-6 sm:p-8 mt-8">
        <h2 className="font-serif-h text-2xl text-[#577731] mb-5 flex items-center gap-3"><Trophy size={20} className="text-[#b6689a]" /> The Circle Scoreboard</h2>
        <Board board={data.board} me={user.id} />
      </div>
      <History refreshKey={data} practices={data.practices} />
    </div>
  );
};

const History = ({ refreshKey, practices }) => {
  const [weeks, setWeeks] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/accountability/history").then((r) => setWeeks(r.data));
  }, [refreshKey]);
  if (!weeks) return null;
  const chart = [...weeks].slice(0, 12).reverse();
  return (
    <section className="card-soft p-6 sm:p-8 mt-8" data-testid="accountability-history">
      <h2 className="font-serif-h text-2xl text-[#577731]">My Progress Over Time</h2>
      <p className="text-sm text-[#232b1b]/60 mt-1">Only you can see this. Each bar is one week, Saturday to Friday.</p>
      {!weeks.length ? (
        <p className="mt-6 text-sm text-[#232b1b]/60">Your first week is being written now. Check in and watch your path grow.</p>
      ) : (
        <>
          <div className="mt-8 flex items-end gap-3 h-48 border-b border-[#dacac1] pb-1 overflow-x-auto" data-testid="history-chart">
            {chart.map((w) => (
              <button key={w.week_start} type="button" onClick={() => setOpen(open === w.week_start ? null : w.week_start)} data-testid={`history-bar-${w.week_start}`}
                className="flex flex-col items-center justify-end h-full min-w-[44px] group">
                <span className="text-xs font-semibold text-[#577731] mb-1">{w.score}</span>
                <span className={`w-8 rounded-t-xl transition-colors duration-300 ${open === w.week_start ? "bg-[#b6689a]" : "bg-[#577731]/80 group-hover:bg-[#b6689a]"}`} style={{ height: `${Math.max(w.score * 10, 6)}%` }} />
                <span className="text-[10px] text-[#232b1b]/55 mt-2 whitespace-nowrap">{fmt(w.week_start, { month: "short", day: "numeric" })}</span>
              </button>
            ))}
          </div>
          <ul className="mt-6 divide-y divide-[#dacac1]/70">
            {weeks.map((w) => (
              <li key={w.week_start} data-testid={`history-week-${w.week_start}`}>
                <button type="button" onClick={() => setOpen(open === w.week_start ? null : w.week_start)} className="w-full flex items-center justify-between py-3 text-sm hover:text-[#b6689a] transition-colors">
                  <span>Week of {fmt(w.week_start, { month: "long", day: "numeric", year: "numeric" })}</span>
                  <span className="font-semibold text-[#577731]">{w.score}/10 <span className="font-normal text-[#232b1b]/50">· {w.checks} check-ins</span></span>
                </button>
                {open === w.week_start && (
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 pb-4" data-testid={`history-detail-${w.week_start}`}>
                    {practices.map((p) => (
                      <div key={p.key} className="flex items-center gap-3 text-xs">
                        <span className="flex-1 text-[#232b1b]/75">{p.label}</span>
                        <span className="w-24 h-1.5 rounded-full bg-[#dacac1]/60 overflow-hidden"><span className="block h-full bg-[#b6689a]" style={{ width: `${(w.by_practice[p.key] / 7) * 100}%` }} /></span>
                        <span className="w-8 text-right text-[#232b1b]/60">{w.by_practice[p.key]}/7</span>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};
